import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { useData } from "../context/DataContext";
import PhoneInputField from "../components/PhoneInputField";

const Contact = () => {
    const { settings, addInquiry } = useData();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");

    const submit = (e) => {
        e.preventDefault();
        if (!name || !email || !message) return toast.error("Fill in name, email and message.");
        addInquiry({ name, email, phone, subject, message });
        toast.success("Thanks! We'll get back to you within 24 hours.");
        setName(""); setEmail(""); setPhone(""); setSubject(""); setMessage("");
    };

    return (
        <div className="bg-cream-100 min-h-screen py-12" data-testid="contact-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
                <h1 className="font-display text-4xl text-ink">Get in touch</h1>
                <p className="text-ink-light mt-2 text-sm">We reply to every Kerala enquiry within a few hours.</p>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5 space-y-4">
                        <ContactItem icon={Phone} title="Call us" value={settings.phone} link={`tel:${settings.phone}`} />
                        <ContactItem icon={MessageCircle} title="WhatsApp" value={settings.whatsapp} link={`https://wa.me/${(settings.whatsapp || "").replace(/[^\d]/g, "")}`} tone="teal" />
                        <ContactItem icon={Mail} title="Email" value={settings.email} link={`mailto:${settings.email}`} />
                        <ContactItem icon={MapPin} title="Office address" value={settings.address} />
                    </div>
                    <div className="lg:col-span-7">
                        <form onSubmit={submit} className="bg-white border border-cream-200 rounded-2xl p-6">
                            <h2 className="font-display text-2xl text-ink">Send us a message</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Name</label>
                                    <input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="contact-name" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Email</label>
                                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="contact-email" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Phone</label>
                                    <PhoneInputField value={phone} onChange={setPhone} testId="contact-phone" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Subject</label>
                                    <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="contact-subject" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Message</label>
                                    <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="contact-message" />
                                </div>
                            </div>
                            <button type="submit" className="mt-5 px-6 py-3 rounded-xl bg-saffron-500 hover:bg-saffron-600 text-white font-semibold flex items-center gap-2 transition shadow-md" data-testid="contact-submit">
                                Send message <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContactItem = ({ icon: Icon, title, value, link, tone = "saffron" }) => {
    const Wrapper = link ? "a" : "div";
    return (
        <Wrapper href={link} target={link?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block bg-white border border-cream-200 rounded-2xl p-5 hover:border-blue-300 transition">
            <span className={`w-10 h-10 rounded-lg ${tone === "teal" ? "bg-blue-50 text-blue-600" : "bg-blue-50 text-blue-500"} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" />
            </span>
            <div className="text-xs uppercase tracking-wider text-ink-light font-semibold">{title}</div>
            <div className="text-ink font-semibold mt-1 leading-snug">{value}</div>
        </Wrapper>
    );
};

export default Contact;
